import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ClientLayout from '../../../components/layout/ClientLayout';
import BalanceCard from './components/BalanceCard';
import RecentTransactions from './components/RecentTransactions';
import { useAuth } from '../../../hooks/useAuth';
import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { useSaldo } from '../../../hooks/useSaldo';
import { ITransacao as Transacao, ICliente as Cliente } from '../../../types/firestore.types';
import { criarTransacao } from '@/api/transactions.api';
import { Timestamp } from 'firebase/firestore';
import Spinner from '../../../components/ui/Spinner';
import Alert from '../../../components/ui/Alert';
import PixPayment from '@/components/shared/PixPayment';
import QRCodeScanner from '@/components/shared/QRCodeScanner';
import { formatarMoeda } from '@/utils/currencyUtils';
import PaymentButton from './components/PaymentButton';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PROMOCAO } from '@/constants/promocao';

// Na Promoção de Aniversário 21 anos existe um valor só.
const opcoesRecarga = [
  {
    valor: String(PROMOCAO.valorCompra),
    label: formatarMoeda(PROMOCAO.valorCompra),
    bonus: '+100% cashback',
    valorFinal: PROMOCAO.valorTotal,
  },
];

interface StatusPromocao {
  carregando: boolean;
  ativa: boolean;
  jaParticipou: boolean;
  erro: string | null;
}

const ClientDashboardPage: React.FC = () => {
  const [valorDigitado, setValorDigitado] = useState<string | number>('');
  const [valorConfirmado, setValorConfirmado] = useState(false);
  const [statusPromocao, setStatusPromocao] = useState<StatusPromocao>({
    carregando: false, ativa: false, jaParticipou: false, erro: null
  });
  const [loading, setLoading] = useState(true);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isScannerActive, setIsScannerActive] = useState(false);
  const auth = useAuth();
  const clienteId = auth.usuario?.id;
  const [alerta, setAlerta] = useState<{ tipo: 'success' | 'error' | 'warning'; mensagem: string } | null>(null);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);

  const exibirAlerta = useCallback((tipo: 'success' | 'error' | 'warning', mensagem: string) => {
    setAlerta({ tipo, mensagem });
    setTimeout(() => setAlerta(null), 5000);
  }, []);

  const getStorageKey = (userId: string) => `pending_pix_transaction_${userId}`;

  // Mesma chave e validade (10 min) do cache do PixPayment: com pagamento
  // em aberto, o modal reabre direto no QR Code em vez de oferecer outro.
  const temPagamentoPendente = (userId: string): boolean => {
    try {
      const cache = localStorage.getItem(getStorageKey(userId));
      if (!cache) return false;
      const { timestamp } = JSON.parse(cache);
      return typeof timestamp === 'number' && Date.now() - timestamp < 10 * 60 * 1000;
    } catch {
      return false;
    }
  };

  const abrirModalRecarga = useCallback(async () => {
    setIsRechargeModalOpen(true);
    setValorDigitado('');
    setValorConfirmado(false);
    setStatusPromocao({ carregando: true, ativa: false, jaParticipou: false, erro: null });

    try {
      const resultado = await httpsCallable(getFunctions(), 'getPromocaoStatus')();
      const { ativa, jaParticipou } = resultado.data as { ativa: boolean; jaParticipou: boolean };
      setStatusPromocao({ carregando: false, ativa, jaParticipou, erro: null });
    } catch (error) {
      console.error('Erro ao consultar a promoção:', error);
      setStatusPromocao({
        carregando: false, ativa: false, jaParticipou: false,
        erro: 'Não foi possível consultar a promoção agora. Tente novamente.'
      });
    }
  }, []);

  const fecharModalRecarga = () => {
    setIsRechargeModalOpen(false);
    setValorDigitado('');
    setValorConfirmado(false);
  };

  const {
    dados: clienteData,
    carregando: carregandoCliente,
    erro: erroCliente,
  } = useFirestoreQuery<Cliente>(
    'clientes',
    clienteId ? [{ campo: 'id', operador: '==', valor: clienteId }] : [],
    null,
    1,
    !!clienteId
  );

  const cliente = useMemo(() => clienteData && clienteData.length > 0 ? clienteData[0] : null, [clienteData]);

  const {
    dados: todasTransacoes,
    carregando: carregandoTransacoes,
    erro: erroTransacoes,
    refetch: refetchTransacoes,
  } = useFirestoreQuery<Transacao>(
    'transacoes',
    clienteId ? [{ campo: 'userId', operador: '==', valor: clienteId }] : [],
    { campo: 'criado_em', direcao: 'desc' },
    null,
    !!clienteId
  );

  // Saldo disponível em reais, para o QR antigo (ver comentário em pagarQrAntigo).
  const { saldoDisponivel } = useSaldo(todasTransacoes || []);

  // Pagamento de cobrança do caixa: QR escaneado ou código digitado (o caixa
  // pode mandar o código pelo WhatsApp). Quem confere o saldo e debita é o
  // servidor; o app só mostra a cobrança e pede a confirmação.
  const [codigoDigitado, setCodigoDigitado] = useState('');
  const [cobrancaParaPagar, setCobrancaParaPagar] = useState<{
    cobrancaId: string; valor: number; numeroComanda: string; unidadeNome: string;
  } | null>(null);
  const [processandoCobranca, setProcessandoCobranca] = useState(false);

  // Cleanup da câmera
  useEffect(() => {
    return () => {
      if (isScannerActive) {
        setIsScannerActive(false);
      }
    };
  }, [isScannerActive]);

  const openScanner = () => {
    setCodigoDigitado('');
    setCobrancaParaPagar(null);
    setIsScannerActive(true);
    setIsScannerOpen(true);
  };

  const closeScanner = () => {
    setIsScannerActive(false);
    setIsScannerOpen(false);
    setCobrancaParaPagar(null);
    setCodigoDigitado('');
    refetchTransacoes();
  };

  const handleScanError = useCallback((error: Error) => {
    console.error('Erro ao escanear QR Code:', error);
    exibirAlerta('error', `Erro ao escanear QR Code: ${error.message}`);
  }, [exibirAlerta]);

  // `daCamera`: se a cobrança não for encontrada, a câmera volta a ler.
  const abrirCobranca = useCallback(async (dados: { cobrancaId?: string; codigo?: string }, daCamera = false) => {
    setProcessandoCobranca(true);
    let encontrada = false;
    try {
      const resultado = await httpsCallable(getFunctions(), 'consultarCobranca')(dados);
      const { success, error, cobranca } = resultado.data as {
        success: boolean;
        error?: string;
        cobranca?: { cobrancaId: string; valor: number; numeroComanda: string; unidadeNome: string };
      };
      if (!success || !cobranca) {
        exibirAlerta('error', error || 'Código não encontrado. Confira com o caixa.');
        return;
      }
      encontrada = true;
      setIsScannerActive(false);
      setCobrancaParaPagar(cobranca);
    } catch (error) {
      console.error('Erro ao consultar cobrança:', error);
      exibirAlerta('error', 'Não foi possível consultar a cobrança. Tente novamente.');
    } finally {
      setProcessandoCobranca(false);
      if (!encontrada && daCamera) setIsScannerActive(true);
    }
  }, [exibirAlerta]);

  const confirmarPagamento = async () => {
    if (!cobrancaParaPagar) return;
    setProcessandoCobranca(true);
    try {
      const resultado = await httpsCallable(getFunctions(), 'pagarCobranca')({ cobrancaId: cobrancaParaPagar.cobrancaId });
      const { success, error } = resultado.data as { success: boolean; error?: string };
      if (success) {
        exibirAlerta('success', `✅ Pagamento de ${formatarMoeda(cobrancaParaPagar.valor)} confirmado!`);
      } else {
        exibirAlerta('error', error || 'Pagamento não aprovado. Procure o caixa.');
      }
      closeScanner();
    } catch (error) {
      console.error('Erro ao pagar cobrança:', error);
      exibirAlerta('error', 'Não foi possível concluir o pagamento. Tente novamente.');
    } finally {
      setProcessandoCobranca(false);
    }
  };

  // QR do caixa que ainda não foi atualizado: em vez de recusar, repete o
  // caminho antigo, em que o próprio app cria o débito pendente e o caixa
  // confirma. É a ponte para os caixas rodando a versão em cache.
  const pagarQrAntigo = useCallback(async (dados: Record<string, unknown>) => {
    if (!clienteId) {
      exibirAlerta('error', 'Usuário não autenticado.');
      return;
    }

    const paymentIntentId = String(dados.paymentIntentId ?? dados.id ?? '');
    const valorNumerico = parseFloat(String(dados.valor ?? dados.v ?? ''));
    const numeroComanda = String(dados.numeroComanda ?? dados.cmd ?? '');

    if (!paymentIntentId || !Number.isFinite(valorNumerico) || valorNumerico <= 0) {
      exibirAlerta('error', 'QR Code do caixa inválido. Peça um novo QR Code.');
      return;
    }

    // Comparação em centavos: em reais o float barrava o valor exato do saldo.
    const saldoEmCentavos = Math.round(saldoDisponivel * 100);
    const valorEmCentavos = Math.round(valorNumerico * 100);
    if (saldoEmCentavos < valorEmCentavos) {
      exibirAlerta('error', `Saldo insuficiente! Você tem ${formatarMoeda(saldoDisponivel)} mas precisa de ${formatarMoeda(valorNumerico)}.`);
      return;
    }

    try {
      await criarTransacao({
        cliente_id: clienteId,
        userId: clienteId,
        unidade_id: String(dados.unidadeId ?? dados.un ?? 'unidade_desconhecida'),
        operador_id: String(dados.operadorId ?? dados.op ?? 'operador_desconhecido'),
        tipo_operacao: 'debito',
        valor: valorNumerico,
        numero_comanda: numeroComanda || null,
        data_hora: Timestamp.now(),
        validated: false,
        qr_code: null,
        campanha_id: null,
        paymentIntentId,
        status: 'pendente_confirmacao',
        initialBalance: saldoDisponivel,
        descricao: `Pagamento via QR Code - Comanda: ${numeroComanda || 'N/A'}`,
      } as Parameters<typeof criarTransacao>[0]);

      exibirAlerta('success', `✅ Pagamento de ${formatarMoeda(valorNumerico)} iniciado! Aguarde a confirmação do caixa.`);
      setIsScannerActive(false);
      setIsScannerOpen(false);
      refetchTransacoes();
    } catch (error) {
      console.error('Erro ao registrar débito do QR antigo:', error);
      exibirAlerta('error', 'Não foi possível registrar o pagamento. Tente novamente.');
    }
  }, [clienteId, saldoDisponivel, refetchTransacoes, exibirAlerta]);

  const handleScanSuccess = useCallback((qrData: string) => {
    setIsScannerActive(false);
    try {
      const dados = JSON.parse(qrData);
      // Cobrança: o QR traz `c` (id) e `k` (código), também quando vem no formato antigo.
      if ((dados?.t === 'tucoins_cobranca' || dados?.c) && (dados.c || dados.k)) {
        abrirCobranca({ cobrancaId: dados.c, codigo: dados.k }, true);
        return;
      }
      // QR de um caixa que ainda está na versão antiga: paga pelo caminho
      // antigo, para o cliente não ficar na mão enquanto os caixas atualizam.
      if (dados?.paymentIntentId || dados?.tipo_operacao === 'debito' || dados?.t === 'tucoins_debito') {
        pagarQrAntigo(dados);
        return;
      }
    } catch {
      // Não é JSON: pode ser o próprio código de pagamento.
      if (/^[A-Za-z0-9]{6}$/.test(qrData.trim())) {
        abrirCobranca({ codigo: qrData.trim() }, true);
        return;
      }
    }
    exibirAlerta('warning', 'QR Code inválido ou não reconhecido.');
    setIsScannerActive(true);
  }, [abrirCobranca, exibirAlerta]);

  useEffect(() => {
    if (auth.usuario) {
      setLoading(false);
    }
  }, [auth.usuario]);

  if (carregandoCliente || carregandoTransacoes || loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center min-h-[calc(100vh-80px)]">
          <Spinner size="lg" color="primary" />
        </div>
      </ClientLayout>
    );
  }

  if (erroCliente || erroTransacoes || !cliente) {
    return (
      <ClientLayout>
        <div className="p-4 sm:p-3 lg:p-2 mx-auto">
          <Alert tipo="error" mensagem="Não foi possível carregar os dados. Tente novamente mais tarde." />
        </div>
      </ClientLayout>
    );
  }

  const valor = parseFloat(valorDigitado as string);
  const mostrarPagamento = valorConfirmado || temPagamentoPendente(cliente.id);

  return (
    <ClientLayout>
      {/* Background Gradient */}
      <div
        className="fixed inset-0 pointer-events-none bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/Background-secao2.png')",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="container mx-auto space-y-8 relative z-10 px-1"
      >
        <AnimatePresence>
          {alerta && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className='fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4'
            >
              <Alert tipo={alerta.tipo} mensagem={alerta.mensagem} onClose={() => setAlerta(null)} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h1 className="text-3xl font-bold text-white mb-2 text-center lg:text-left">
            Olá, <span className="text-primary bg-clip-text">{cliente.nome.toUpperCase()}</span>!
          </h1>
          <p className="text-gray-400 text-center lg:text-left">Bem-vindo ao seu painel de controle</p>
        </motion.div>

        {/* Balance and Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {/* Balance Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <BalanceCard />
          </motion.div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-1 gap-2">
            {/* Botão Recarregar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              
              <PaymentButton
                title='Adicionar Tugas Coins'
                subtitle="Adicione saldo via PIX"
                icon={
                  <div className="p-2 bg-primary rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                }
                onClick={abrirModalRecarga}
                variant="primary"
              />
            </motion.div>

            {/* Botão Pagar no Caixa */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <PaymentButton
                title="Pagar no Caixa"
                subtitle="QR Code ou código"
                icon={
                  <div className="p-2 bg-primary rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </div>
                }
                onClick={openScanner}
                variant="accent"
              />
            </motion.div>
          </div>
        </div>

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8"
        >
          <RecentTransactions transacoes={todasTransacoes} showAllStatus={true} />
        </motion.div>
      </motion.div>

      {/* Modal de Recarga */}
      <AnimatePresence>
        {isRechargeModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            // Com o QR Code na tela, clicar fora não fecha: fechar cancela o PIX.
            onClick={() => { if (!mostrarPagamento) fecharModalRecarga(); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background rounded-2xl border border-primary/20 shadow-xl max-w-md w-full overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Botão de fechar */}
              <button
                onClick={fecharModalRecarga}
                className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-200 transition-colors duration-200 bg-gray-800/50 hover:bg-gray-700/50 rounded-full p-1.5"
                aria-label="Fechar modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {!mostrarPagamento && statusPromocao.carregando ? (
                <div className="flex justify-center items-center p-16">
                  <Spinner size="lg" color="primary" />
                </div>
              ) : !mostrarPagamento && statusPromocao.erro ? (
                <div className="p-8 text-center space-y-4">
                  <p className="text-gray-300">{statusPromocao.erro}</p>
                  <button
                    onClick={abrirModalRecarga}
                    className="py-3 px-6 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors"
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : !mostrarPagamento && statusPromocao.jaParticipou ? (
                <div className="flex items-center justify-center p-6 border-b border-primary/20">
                  <div>
                    <motion.img
                      src='/clube.png'
                      alt="TugasCoins Logo"
                      className="h-40 object-contain"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    />
                    <h3 className="text-2xl font-bold text-gray-100">Você já participou!</h3>
                    <p className="text-gray-500 text-sm mt-1">A {PROMOCAO.nome} é limitada a 1 compra por conta.</p>
                  </div>
                </div>
              ) : !mostrarPagamento && !statusPromocao.ativa ? (
                <div className="flex items-center justify-center p-6 border-b border-primary/20">
                  <div>
                    <motion.img
                      src='/clube.png'
                      alt="TugasCoins Logo"
                      className="h-40 object-contain"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    />
                    <h3 className="text-2xl font-bold text-gray-100">Promoção Encerrada!</h3>
                    <p className="text-gray-500 text-sm mt-1">Aguarde novas promoções em breve.</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Header simples e clean */}
                  <div className="flex items-center justify-between p-6 border-b border-primary/20">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-100">Recarregar Saldo</h3>
                      <p className="text-gray-500 text-sm mt-1">Escolha o valor para adicionar Tugas Coins</p>
                    </div>

                  </div>

                  <div className="p-6">
                    {!mostrarPagamento ? (
                      <div className="space-y-6">
                        {/* Botões de Valor - Design Clean */}
                        <div className="grid grid-cols-2 gap-3">
                          {opcoesRecarga.map((opcao) => (
                            <button
                              key={opcao.valor}
                              onClick={() => setValorDigitado(opcao.valor)}
                              className={`
                      p-4 rounded-xl bg-gradient-to-r from-[#c5a663] via-[#cebc6e] to-[#c5a663] transition-all duration-200 text-left
                      ${valorDigitado === opcao.valor
                                  ? 'border-green-500 bg-amber-300 shadow-sm'
                                  : 'border-gray-200 hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]'
                                }
                    `}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="text-white font-semibold">{opcao.label}</div>
                                  <div className="text-green-600 text-sm font-medium mt-1">{opcao.bonus}</div>
                                </div>
                                {valorDigitado === opcao.valor && (
                                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* Preview do Saldo */}
                        {valorDigitado && (
                          <div className="bg-primary rounded-xl p-4 border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                                <div>
                                  <div className="text-white font-semibold">Saldo Final</div>
                                  <div className="text-white text-sm">Com cashback</div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-white font-bold text-lg">
                                  TC {opcoesRecarga.find(op => op.valor === valorDigitado)?.valorFinal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-green-600 text-sm font-medium">
                                  {opcoesRecarga.find(op => op.valor === valorDigitado)?.bonus}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Informação adicional */}
                        <div className="bg-primary rounded-lg p-3 border border-primary/30">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 text-white mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-white text-sm">
                              O saldo será creditado em até 48 horas após a confirmação do pagamento.
                              Limitado a 1 compra por conta. Válido para consumo no local ou retirada e para evento na Cave.
                              Não vale para delivery de chope e delivery de pizza.
                            </p>
                          </div>
                        </div>

                        {/* Botões de Ação */}
                        <div className="flex gap-3 pt-2">
                          <button
                            onClick={fecharModalRecarga}
                            className="flex-1 py-3 px-4 border border-gray-300 text-white rounded-xl bg-danger hover:bg-red-700 transition-colors font-medium"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => {
                              const valorNumerico = parseFloat(valorDigitado as string);
                              if (valorNumerico > 0) {
                                setValorConfirmado(true);
                              } else {
                                exibirAlerta('warning', 'Selecione um valor para recarregar.');
                              }
                            }}
                            disabled={!valorDigitado}
                            className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors"
                          >
                            Continuar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-4">
                        <PixPayment
                          merchantInfo={{ nome: 'Clube Mr Tugas', chavePix: '21857982-cbe5-4f08-9abb-ca55791215d6', cidade: 'Juiz De Fora' }}
                          userId={cliente.id}
                          valor={valor}
                          onClose={fecharModalRecarga}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal do Scanner */}
      <AnimatePresence>
        {isScannerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-background rounded-2xl shadow-2xl max-w-md w-full mx-4 border border-primary/20"
            >
              {cobrancaParaPagar ? (
                <div className="p-6 text-center space-y-4">
                  <h3 className="text-xl font-bold text-white">Confirmar pagamento</h3>
                  <p className="text-gray-300">
                    Pagar <span className="text-primary font-bold text-2xl">{formatarMoeda(cobrancaParaPagar.valor)}</span>
                  </p>
                  <p className="text-gray-300">
                    em {cobrancaParaPagar.unidadeNome}
                    {cobrancaParaPagar.numeroComanda && <> — comanda {cobrancaParaPagar.numeroComanda}</>}
                  </p>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={closeScanner}
                      disabled={processandoCobranca}
                      className="flex-1 py-3 px-4 border border-gray-300 text-white rounded-xl bg-danger hover:bg-red-700 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={confirmarPagamento}
                      disabled={processandoCobranca}
                      className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors"
                    >
                      {processandoCobranca ? 'Pagando...' : 'Confirmar'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <QRCodeScanner
                    onScan={(data) => {
                      handleScanSuccess(data);
                    }}
                    onError={handleScanError}
                    onClose={closeScanner}
                    isActive={isScannerActive}
                    title="Escanear QR Code do Caixa"
                    description="Aponte a câmera para o QR Code gerado pelo caixa."
                  />

                  {/* Código enviado pelo caixa (por exemplo, pelo WhatsApp) */}
                  <div className="px-6 pb-6">
                    <p className="text-gray-300 text-sm mb-2 text-center">Ou digite o código de pagamento</p>
                    <div className="flex gap-2">
                      <input
                        value={codigoDigitado}
                        onChange={(e) => setCodigoDigitado(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                        placeholder="CÓDIGO"
                        maxLength={6}
                        className="flex-1 min-w-0 rounded-xl bg-background text-text border border-primary/30 px-3 py-2 text-center font-mono text-lg tracking-widest uppercase"
                      />
                      <button
                        onClick={() => abrirCobranca({ codigo: codigoDigitado })}
                        disabled={codigoDigitado.length !== 6 || processandoCobranca}
                        className="py-2 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors"
                      >
                        {processandoCobranca ? '...' : 'Continuar'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ClientLayout>
  );
};

export default ClientDashboardPage;